/**
 * TabletTireMagazzino — Ricerca e gestione magazzino pneumatici da tablet.
 */
import { useState } from 'react'
import { Card, Input, Typography, Tag, Badge, Drawer, Spin, Alert, Space, Button, theme } from 'antd'
import { SearchOutlined, QrcodeOutlined } from '@ant-design/icons'
import { useSetPneumatici } from '@/hooks/useTireService'
import { API_ENDPOINTS } from '@/config/api'
import axiosInstance from '@/lib/axios'
import type { SetPneumatici, StatoSetType } from '@/types/tireService'

const { Title, Text } = Typography

const STATO_BADGE: Record<StatoSetType, 'success' | 'processing' | 'error'> = {
  STOCCATO: 'success',
  MONTATO: 'processing',
  DISMESSO: 'error',
}

const STAGIONE_COLOR: Record<string, string> = {
  INVERNALE: 'blue',
  ESTIVA: 'orange',
  '4STAGIONI': 'green',
}

export default function TabletTireMagazzino() {
  const { token } = theme.useToken()
  const [search, setSearch] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [selectedSet, setSelectedSet] = useState<SetPneumatici | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data: sets = [], isLoading } = useSetPneumatici(
    submitted ? { misura: submitted } : undefined
  )

  // Filtra anche per targa nel client se i dati includono vehicle
  const filtered = submitted
    ? sets.filter(s =>
        s.vehicle?.targa?.toUpperCase().includes(submitted.toUpperCase()) ||
        s.misura?.toLowerCase().includes(submitted.toLowerCase()) ||
        s.codice?.toUpperCase().includes(submitted.toUpperCase())
      )
    : sets

  const handleSearch = (value: string) => {
    setSubmitted(value.trim())
  }

  const handleOpenSet = (s: SetPneumatici) => {
    setSelectedSet(s)
    setDrawerOpen(true)
  }

  const qrUrl = selectedSet
    ? `${axiosInstance.defaults.baseURL}${API_ENDPOINTS.TIRE_SERVICE.SET_QR_LABEL(selectedSet.id)}`
    : ''

  return (
    <div style={{ minHeight: '100vh', background: token.colorBgLayout, padding: '24px 16px' }}>
      {/* Header */}
      <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
        Magazzino Pneumatici
      </Title>

      {/* Ricerca */}
      <Input.Search
        size="large"
        placeholder="Cerca per targa, misura o codice…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        onSearch={handleSearch}
        enterButton={<Button size="large" type="primary" icon={<SearchOutlined />}>Cerca</Button>}
        style={{ marginBottom: 24, fontSize: 17 }}
        allowClear
      />

      {/* Loading */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {/* No results */}
      {!isLoading && submitted && filtered.length === 0 && (
        <Alert type="info" showIcon message={`Nessun set trovato per "${submitted}"`} />
      )}

      {/* Lista set */}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {filtered.map(s => (
          <Card
            key={s.id}
            hoverable
            onClick={() => handleOpenSet(s)}
            size="small"
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                {s.vehicle && (
                  <Text strong style={{ fontSize: 18, display: 'block' }}>
                    {s.vehicle.targa}
                  </Text>
                )}
                <Text style={{ fontSize: 15, display: 'block' }}>
                  {s.misura}
                  {s.marca && <> — {s.marca}</>}
                </Text>
                <Tag color={STAGIONE_COLOR[s.stagione]} style={{ marginTop: 4 }}>
                  {s.stagione}
                </Tag>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Badge status={STATO_BADGE[s.stato]} text={s.stato} style={{ fontSize: 14 }} />
                {s.posizione && (
                  <div style={{ marginTop: 8 }}>
                    <Tag color="blue" style={{ fontSize: 14 }}>
                      📦 {s.posizione}
                    </Tag>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </Space>

      {/* Drawer dettaglio + QR */}
      <Drawer
        title={selectedSet ? `Set ${selectedSet.codice}` : 'Dettaglio'}
        placement="bottom"
        height="70vh"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Button
            icon={<QrcodeOutlined />}
            type="primary"
            onClick={() => window.open(qrUrl, '_blank')}
          >
            Apri QR
          </Button>
        }
      >
        {selectedSet && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text type="secondary">Misura</Text>
              <Title level={4} style={{ margin: 0 }}>{selectedSet.misura}</Title>
            </div>
            <div>
              <Text type="secondary">Stagione</Text>
              <div>
                <Tag color={STAGIONE_COLOR[selectedSet.stagione]} style={{ fontSize: 15, padding: '4px 10px' }}>
                  {selectedSet.stagione}
                </Tag>
              </div>
            </div>
            <div>
              <Text type="secondary">Stato</Text>
              <div>
                <Badge status={STATO_BADGE[selectedSet.stato]} text={<Text strong style={{ fontSize: 16 }}>{selectedSet.stato}</Text>} />
              </div>
            </div>
            {selectedSet.posizione && (
              <div>
                <Text type="secondary">Posizione scaffale</Text>
                <Title level={3} style={{ margin: 0, color: '#1677ff' }}>{selectedSet.posizione}</Title>
              </div>
            )}
            {selectedSet.profondita_mm && (
              <div>
                <Text type="secondary">Profondità battistrada</Text>
                <Text strong style={{ fontSize: 16, display: 'block' }}>{selectedSet.profondita_mm} mm</Text>
              </div>
            )}
            {/* QR inline */}
            <div style={{ textAlign: 'center' }}>
              <img
                src={qrUrl}
                alt="QR Code"
                style={{ maxWidth: 200, border: '1px solid #d9d9d9', borderRadius: 8 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  )
}
